import React from 'react';
import { BodyText, Gutter, Tag } from '@gaiads/telia-react-component-library';
import { useTranslation } from 'react-i18next';
import { Card, CardProps, GenericTable } from '@cdaas/reusable-components-daas';
import { AdminProductVariant, Country, Material } from '@cdaas/api-clients/dist/product-service-openapi';

import { combineVariantName } from '../../../utils/products';
import MaterialCodeVariantDetails from '../MaterialCodeVariantDetails/MaterialCodeVariantDetails';

interface MaterialCodesCardProps {
  variants: AdminProductVariant[];
  variantPropertyValues?: Record<number, string[]>;
  productName?: string;
  countryId: number;
  countries: Country[];
  onEditClick: (id: AdminProductVariant['id']) => void;
}

const isMaterialCodesValid = (variants: AdminProductVariant[], countryId: number) => {
  let isValid = true;

  if (variants.length === 0) {
    return true;
  }

  variants.forEach(variant => {
    const { materials } = variant;
    const materialsByCountry = materials?.filter((material: Material) => material.country_id === countryId) || [];
    if (materialsByCountry.length === 0 || materialsByCountry.length > 1) {
      isValid = false;
    }
  });

  return isValid;
};

const MaterialCodesCard = ({ variants, variantPropertyValues, productName, countryId, countries, onEditClick }: MaterialCodesCardProps) => {
  const { t } = useTranslation('productscreen');
  const cardTag: CardProps['tag'] = !isMaterialCodesValid(variants, countryId) ? { text: t('materialCodes.updateRequired') } : undefined;
  const cardProps: CardProps = {
    isInitiallyOpen: true,
    title: t('materialCodes.title'),
    tag: cardTag,
    wrapperDivCustomProps: { backgroundColor: 'white', margin: { bottom: 'sm' } },
  };

  const formatMaterialCodeColumn = (materials: Material[]) => {
    const materialCodes: Material[] = materials?.filter((material: Material) => material.country_id === countryId) || [];
    if (materialCodes.length === 1) {
      return <BodyText title={materialCodes[0].code}>{materialCodes[0].code}</BodyText>;
    } else if (materialCodes.length > 1) {
      return <Tag color="red600">{t('materialCodes.duplicateCodes')}</Tag>;
    } else {
      return <Tag color="red600">{t('materialCodes.missingCode')}</Tag>;
    }
  };

  const formatVariantName = (variant: AdminProductVariant) => {
    const formatedVariantNames = (variantPropertyValues && combineVariantName(variantPropertyValues[variant.id])) || '';
    const name = formatedVariantNames ? formatedVariantNames : productName;
    return <BodyText title={name}>{name}</BodyText>;
  };

  const rowConfig = [
    { formatValue: (value: unknown, variant: AdminProductVariant) => formatVariantName(variant) },
    { key: 'materials', formatValue: (value: Material[]) => formatMaterialCodeColumn(value) },
  ];

  return (
    <Card {...cardProps} key={'variants'}>
      <BodyText color={'gray700'} padding={{ top: 'sm' }}>
        {t('materialCodes.infoText')}
      </BodyText>
      <Gutter size="md" />
      <GenericTable
        loading="none"
        inputData={variants}
        noDataText={t('materialCodes.noMaterialCodes')}
        rowConfigs={rowConfig}
        isGenericTableInsideCard={true}
        chevronConfig={{
          accordionContent: function AccordionContent(variant) {
            return (
              <MaterialCodeVariantDetails
                variant={variant}
                variantPropertyValues={variantPropertyValues}
                productName={productName}
                countryId={countryId}
                countries={countries}
              />
            );
          },
        }}
        buttonConfig={{
          buttonClickCallback: row => onEditClick(row.id),
          buttonProps: { variant: 'secondary' },
          hideAtBreakpoint: undefined,
          name: t('materialCodes.editButton'),
        }}
      />
    </Card>
  );
};

export default MaterialCodesCard;
